/**
 * ProviderServiceCard Component
 * Feature: 009-provider-ui-pixel-perfect
 * Tasks: T109, T112-T116 - Service card sub-component
 * 
 * Displays a single provider service with name, description, pricing, and price range indicator.
 */

import { CheckCircle2 } from 'lucide-react';
import type { ProviderService } from '../types/provider-profile.types';

type ProviderServiceCardProps = {
  service: ProviderService;
};

export const ProviderServiceCard = ({ service }: ProviderServiceCardProps) => {
  const {
    serviceName,
    serviceDescription,
    servicePrice,
    priceType,
    priceRangeIndicator,
  } = service;

  // Determine if we should show "Contact for pricing"
  const showContactForPricing = !servicePrice || servicePrice.toLowerCase().includes('contact');

  return (
    <div className="p-4 border border-gray-200 rounded-lg hover:border-[#026857] transition-colors">
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="w-8 h-8 bg-[#026857]/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
          <CheckCircle2 className="w-4 h-4 text-[#026857]" data-testid="service-icon" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header with Name and Price Range */}
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="font-semibold text-gray-900">{serviceName}</h3>
            <div className="text-right whitespace-nowrap">
              {/* Price Range Indicator */}
              {priceRangeIndicator && (
                <div className="text-lg text-gray-900">{priceRangeIndicator}</div>
              )}
              {/* Price Details */}
              {showContactForPricing ? (
                <div className="text-xs text-[#026857]">Contact for pricing</div>
              ) : (
                <div className="text-xs text-gray-500">
                  {servicePrice} {priceType}
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {serviceDescription && (
            <p className="text-sm text-gray-600">{serviceDescription}</p>
          )}
        </div>
      </div>
    </div>
  );
};

